import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { TableCell, TableRow, Typography } from '@mui/material';

import Table from '../../components/Table';
import EmptyState from '../../components/EmptyState';
import Pagination from '../../components/Pagination';
import SearchInput from '../../components/SearchInput';
import FilterLabel from '../../components/Filter/FilterLabel';
import FilterInput from '../../components/Filter/FilterInput';
import Modal from '../../components/Modal';
import { BlockBgIcon, ViewBgIcon, UnBlockBgIcon } from '../../utils/Icons';
import { renderTextEllipsis, setPageMainTitle, textContainOnlySpaces } from '../../utils/Helpers';
import messages from '../../assets/locale/messages';
import { getCustomersRequest, getCustomersResponse, blockOrUnblockCustomer, getRequestStatus } from '../../store/Customers/actions';
import { ROUTE_PATHS } from '../../utils/RoutesPaths';
import { isSuperAdmin } from '../../utils/Permissions';
import './Customers.scss';

const Customers = () => {
  const lang = useSelector((state) => state.locale.lang);
  const isLoading = useSelector((state) => state.loader);
  const list = useSelector((state) => state.customers.list);
  const meta = useSelector((state) => state.customers.meta);
  const requestStatus = useSelector((state) => state.customers.requestStatus);

  const dispatch = useDispatch();
  const history = useHistory();

  const { shared, pagesTitles, customers } = messages[lang];

  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [filterCustomerStatus, setFilterCustomerStatus] = useState(null);
  const [statusChanged, setStatusChanged] = useState(false);
  const [showBlockCustomerConfirmationMsg, setShowBlockCustomerConfirmationMsg] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState({})

  const customersStatus = useMemo(
    () => ({
      unblocked: 'unblocked',
      blocked: 'blocked',
    }),
    []
  );
  const providerStatusOptions = useMemo(() => {
    return [
      {
        id: customersStatus.unblocked,
        label: customers.list.statusCases.active,
      },
      {
        id: customersStatus.blocked,
        label: customers.list.statusCases.blocked,
      },
    ];
  }, [lang]);

  useEffect(() => {
    return () => {
      dispatch(
        getCustomersResponse({
          list: [],
          meta: null,
        })
      );
    };
  }, []);

  useEffect(() => {
    setPageMainTitle('customersManagement');
  }, []);

  useEffect(() => {
    getCustomers(page, searchValue, filterCustomerStatus?.id);
  }, [page]);

  useEffect(() => {
    getCustomers(page, searchValue, filterCustomerStatus?.id);
  }, [requestStatus]);

  useEffect(() => {
    if ((searchValue.length >= 3 || searchValue.length === 0) && !textContainOnlySpaces(searchValue)) {
      setPage(1);
      getCustomers(1, searchValue, filterCustomerStatus?.id);
    }
  }, [searchValue]);

  useEffect(() => {
    setPage(1);
    getCustomers(1, searchValue, filterCustomerStatus?.id);
  }, [filterCustomerStatus, statusChanged]);

  useEffect(() => {
    if (filterCustomerStatus) {
      setFilterCustomerStatus(providerStatusOptions.find((option) => option.id === filterCustomerStatus.id));
      setStatusChanged(false);
    }
  }, [lang, providerStatusOptions]);

  const getCustomers = (pageNo, search, status) => {
    dispatch(
      getCustomersRequest({
        page: pageNo,
        items: 10,
        query: search,
        status: status,
      })
    );
  };

  const handleViewCustomer = (id) => {
    history.push(ROUTE_PATHS.viewCustomer.replace(':id', id));
  };

  const handleBlockOrUnblockCustomer = (id,status) => {
    dispatch(blockOrUnblockCustomer({
      id,
      data:{
        status
      }
    }));
    dispatch(getRequestStatus(false));
  };
  
  const handleConfirmBlockCustomer = () => {
    setShowBlockCustomerConfirmationMsg(false);
    handleBlockOrUnblockCustomer(selectedCustomer.id, 'blocked');
  };

  const RenderTitle = (title) => {
    switch (title) {
      case customersStatus.unblocked:
        return `${customers.list.statusCases.active}`;
      case customersStatus.blocked:
        return `${customers.list.statusCases.blocked}`;
      default:
        return `-`;
    }
  };

  const renderHeaderSection = (showActions) => (
    <>
      <div className="customers-header d-flex justify-content-between mb-4">
        <div className="title-search d-flex flex-column flex-md-row">
          <Typography variant="h4" sx={{ flexGrow: 1 }} className={`page-title ${lang === 'en' ? 'me-5' : 'ms-5'}`}>
            {pagesTitles.customers}
          </Typography>
          {showActions && (
            <SearchInput
              name="search"
              value={searchValue}
              placeholder={customers.list.search}
              onChange={(value) => {
                setSearchValue(value);
              }}
            />
          )}
        </div>
      </div>
      {showActions && (
        <div className="filter-label-wrapper d-flex align-items-center mb-4 w-50 gap-2 flex-wrap flex-md-nowrap w-100">
          <FilterLabel />
          <FilterInput
            placeholder={customers.list.filter.filtersPlaceHolders.filterByCustomerStatus}
            options={providerStatusOptions}
            value={filterCustomerStatus}
            name="provider status Filter"
            onChange={(value) => {
              setStatusChanged(true);
              setFilterCustomerStatus(value);
            }}
            filterWrapperClass="my-3 my-md-0"
          />
        </div>
      )}
    </>
  );

  const customersList = () => {
    if (!isLoading && list?.length === 0 && !searchValue && filterCustomerStatus === null) {
      return (
        <>
          {renderHeaderSection(false)}
          <div className="bg-white rounded-4 customers-container d-flex justify-content-center align-items-center flex-column">
            <EmptyState
              subTitle={shared.noData}
              description={customers.list.noDataDescription}
              showActionButton={false}
            />
          </div>
        </>
      );
    } else if (!isLoading && (searchValue || filterCustomerStatus !== null) && list?.length === 0) {
      return (
        <>
          {renderHeaderSection(true)}
          <div className="bg-white rounded-4 customers-container d-flex justify-content-center align-items-center flex-column">
            <EmptyState subTitle={shared.noSearch} showActionButton={false} />
          </div>
        </>
      );
    } else {
      return (
        <>
          <div>
            {renderHeaderSection(true)}
            <div className="bg-white rounded-4 customers-table-list">
              <Table
                className="rounded-4"
                headlines={[customers.list.name, customers.list.phoneNumber, customers.list.status]}
                hasActions={true}
                rows={list?.map((row, i) => (
                  <TableRow key={row?.id || i}>
                    <TableCell className="start font-regular text-dark-blue fsize-14 ">
                      {renderTextEllipsis(
                        (row.first_name ? row.first_name : '-') + ' ' + (row.last_name ? row.last_name : '')
                      )}
                    </TableCell>
                    <TableCell className="font-regular text-dark-blue fsize-14 ">
                      ({row.phone_dial_code + ') ' + row.phone_number}
                    </TableCell>
                    <TableCell className="font-regular text-dark-blue fsize-14 ">
                      <div
                        className={`status status-${row.status} px-3 py-2 border-radius-10 text-center position-relative`}>
                        <span className="d-inline-block me-2">{RenderTitle(row.status)}</span>
                      </div>
                    </TableCell>

                    <TableCell className="font-regular text-dark-blue fsize-14">
                      <div className="table-icons-container gap-4">
                        { (isSuperAdmin() && row?.status === customersStatus.unblocked) && (
                          <BlockBgIcon  
                            className="pointer" 
                            onClick={() => {
                              setSelectedCustomer(row);
                              setShowBlockCustomerConfirmationMsg(true);
                            }} 
                          />
                        )}
                        { (isSuperAdmin() && row?.status === customersStatus.blocked) && (
                          <UnBlockBgIcon className="pointer" onClick={() => {handleBlockOrUnblockCustomer(row.id, 'unblocked')}} />
                        )}
                        <ViewBgIcon className="pointer" onClick={() => handleViewCustomer(row.id)} />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              />
              <div className="d-flex justify-content-center py-3">
                {meta?.pages > 1 && (
                  <Pagination
                    count={meta?.pages}
                    page={meta?.currentPage || page}
                    handleChange={(event, page) => {
                      setPage(page);
                    }}
                    defaultPage={1}
                  />
                )}
              </div>
              <Modal
                open={showBlockCustomerConfirmationMsg}
                handleClose={()=>{setShowBlockCustomerConfirmationMsg(false)}}
                title={<span className="fsize-26 fweight-400">{customers.list.blockCustomer}</span>}
                contentClass="mb-3 pb-2 text-primary"
                content={
                  <span>
                    {customers.list.blockCustomerMsg}
                    <div className="my-3">
                      <span className="text-secondary">{customers.list.note}: </span>
                      {customers.list.blockCustomerNote}
                    </div>
                  </span>
                }
                confirmBtnTxt={shared.confirm}
                cancelBtnTxt={shared.cancel}
                isContentText={false}
                handleConfirm={handleConfirmBlockCustomer}
                handleCancel={()=>{setShowBlockCustomerConfirmationMsg(false)}}
                maxWidth="sm"
                actionsClass=" px-4"
                confirmBtnClass="btn-secondary fsize-14 text-white py-2 px-5"
                cancelBtnClass="fsize-14 py-2 text-secondary px-5 "
              />
            </div>
          </div>
        </>
      );
    }
  };

  return <div className="customers-page">{customersList()}</div>;
};

export default Customers;
